import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useAuthStore } from '../stores/auth.store';
import { useWorkspaceStore } from '../stores/workspace.store';
import { TermsModal } from '../components/TermsModal';
import { InteractiveBackground } from '../components/InteractiveBackground';

export const Route = createFileRoute('/register-invite/$token')({
  component: RegisterInvitePage,
});

enum ProfessionalProfile {
  SOCIO = 'SOCIO',
  ADVOGADO_ASSOCIADO = 'ADVOGADO_ASSOCIADO',
  ADVOGADO_CELETISTA = 'ADVOGADO_CELETISTA',
  ADVOGADO_AUTONOMO = 'ADVOGADO_AUTONOMO',
  BACHAREL = 'BACHAREL',
  ESTAGIARIO = 'ESTAGIARIO',
  TRAINEE = 'TRAINEE',
  ADMINISTRATIVO = 'ADMINISTRATIVO',
  FINANCEIRO = 'FINANCEIRO',
}

const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const PROFESSIONAL_PROFILES = [
  { value: ProfessionalProfile.SOCIO, label: 'Sócio' },
  { value: ProfessionalProfile.ADVOGADO_ASSOCIADO, label: 'Advogado Associado' },
  { value: ProfessionalProfile.ADVOGADO_CELETISTA, label: 'Advogado Celetista' },
  { value: ProfessionalProfile.ADVOGADO_AUTONOMO, label: 'Advogado Autônomo' },
  { value: ProfessionalProfile.BACHAREL, label: 'Bacharel' },
  { value: ProfessionalProfile.ESTAGIARIO, label: 'Estagiário' },
  { value: ProfessionalProfile.TRAINEE, label: 'Trainee' },
  { value: ProfessionalProfile.ADMINISTRATIVO, label: 'Administrativo' },
  { value: ProfessionalProfile.FINANCEIRO, label: 'Financeiro' },
];

const TERMS_VERSION = '1.0.0';

const inputStyle = {
  background: '#F8F8F8',
  border: 'none',
  borderRadius: '20px',
  fontFamily: "'Poppins', sans-serif",
  fontSize: '12px',
  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
  color: '#2D3748',
};

const labelStyle = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: '12px',
  color: '#2D3748',
  fontWeight: 500,
};

function RegisterInvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);

  const [form, setForm] = useState({
    fullName: '',
    password: '',
    confirmPassword: '',
    address: '',
    professionalProfile: '' as ProfessionalProfile | '',
    oabNumber: '',
    oabState: '',
    termsAccepted: false,
  });
  const [error, setError] = useState('');
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsType, setTermsType] = useState<'terms' | 'privacy'>('terms');
  const errorRef = useRef<HTMLDivElement>(null);

  const { data: invite, isLoading: isLoadingInvite, error: inviteError } = useQuery({
    queryKey: ['invite', token],
    queryFn: async () => {
      const response = await api.get(`/invites/${token}`);
      return response.data.data;
    },
    retry: false,
  });

  const registerMutation = useMutation({
    mutationFn: async (payload: {
      token: string;
      password: string;
      name: string;
      fullName: string;
      address?: string;
      professionalProfile: string;
      oabNumber?: string;
      oabState?: string;
      termsAccepted: boolean;
      termsVersion: string;
    }) => {
      const response = await api.post('/auth/register-with-invite', payload);
      return response.data.data;
    },
    onSuccess: async (data, variables) => {
      const { user, accessToken, refreshToken, csrfToken } = data;
      setAuth(user, accessToken, refreshToken, csrfToken);
      const workspaceId = invite?.workspaceId;
      if (workspaceId) {
        const workspaceResponse = await api.get(`/workspaces/${workspaceId}`);
        setWorkspace(workspaceResponse.data.data);
        navigate({ to: `/w/${workspaceId}/clients` });
      } else {
        navigate({ to: '/workspaces' });
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Erro ao criar conta');
    },
  });

  const requiresOAB = [
    ProfessionalProfile.SOCIO,
    ProfessionalProfile.ADVOGADO_ASSOCIADO,
    ProfessionalProfile.ADVOGADO_CELETISTA,
    ProfessionalProfile.ADVOGADO_AUTONOMO,
  ].includes(form.professionalProfile as ProfessionalProfile);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    if (!form.termsAccepted) {
      setError('É necessário aceitar os termos de uso e política de privacidade');
      return;
    }
    if (requiresOAB && (!form.oabNumber?.trim() || !form.oabState)) {
      setError('Número da OAB e UF são obrigatórios para este perfil profissional');
      return;
    }

    const name = form.fullName.trim().split(/\s+/)[0] || 'Usuário';
    registerMutation.mutate({
      token,
      password: form.password,
      name,
      fullName: form.fullName.trim(),
      address: form.address?.trim() || undefined,
      professionalProfile: form.professionalProfile,
      oabNumber: form.oabNumber?.trim() || undefined,
      oabState: form.oabState || undefined,
      termsAccepted: form.termsAccepted,
      termsVersion: TERMS_VERSION,
    });
  };

  const openTermsModal = (type: 'terms' | 'privacy') => {
    setTermsType(type);
    if (type === 'terms') setTermsModalOpen(true);
    else setPrivacyModalOpen(true);
  };

  if (isLoadingInvite) {
    return (
      <div className="min-h-screen relative overflow-hidden" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <InteractiveBackground />
        <div className="min-h-screen flex items-center justify-center relative z-10 p-6">
          <div
            className="text-center"
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '48px 32px',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
              maxWidth: '420px',
              width: '100%',
            }}
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#233444' }} />
            <p className="mt-4" style={{ fontSize: '12px', color: '#718096' }}>Carregando informações do convite...</p>
          </div>
        </div>
      </div>
    );
  }

  if (inviteError || !invite) {
    return (
      <div className="min-h-screen relative overflow-hidden" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <InteractiveBackground />
        <div className="min-h-screen flex items-center justify-center relative z-10 p-6">
          <div
            className="text-center"
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '40px 32px',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
              maxWidth: '420px',
              width: '100%',
            }}
          >
            <img src="/logo-preta.svg" alt="Prometeus" className="mx-auto h-10 w-auto mb-6" />
            <h2 className="text-xl font-semibold mb-3" style={{ color: '#DC2626' }}>Convite inválido</h2>
            <p className="mb-6" style={{ fontSize: '12px', color: '#4A5568' }}>
              Este convite não existe, expirou ou já foi utilizado.
            </p>
            <button
              type="button"
              onClick={() => navigate({ to: '/login' })}
              className="px-5 py-2.5 text-white rounded-[20px] transition-colors hover:opacity-90"
              style={{
                backgroundColor: '#233444',
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
              }}
            >
              Ir para Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        input::placeholder,
        select::placeholder {
          color: #2D3748;
          opacity: 0.6;
        }
        select option {
          color: #2D3748;
        }
      `}</style>
      <div className="min-h-screen relative overflow-hidden" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <InteractiveBackground />
        <div className="min-h-screen flex items-center justify-center relative z-10 p-6 sm:p-8">
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              maxHeight: '90vh',
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '32px 28px',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div className="text-center flex-shrink-0 mb-6">
              <img src="/logo-preta.svg" alt="Prometeus" className="mx-auto h-10 w-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2" style={{ color: '#2D3748' }}>Criar conta</h2>
              <p className="text-sm" style={{ color: '#718096' }}>
                Você foi convidado para o workspace <strong>{invite.workspace?.name}</strong>
              </p>
              {invite.role && (
                <p className="mt-1 text-sm" style={{ color: '#718096' }}>
                  Como: <strong>{invite.role.name}</strong>
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-y-auto pr-1" style={{ gap: '20px' }}>
          <div style={{ marginBottom: '4px' }}>
            <label className="block mb-2" style={labelStyle}>
              E-mail
            </label>
            <input
              type="email"
              value={invite.email}
              disabled
              className="w-full px-4 py-2.5 focus:outline-none cursor-not-allowed"
              style={{ ...inputStyle, color: '#718096' }}
            />
            <p className="mt-1 text-xs" style={{ color: '#718096' }}>O e-mail não pode ser alterado</p>
          </div>

          <div>
            <label className="block mb-2" style={labelStyle}>
              Nome completo <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              type="text"
              required
              className="w-full px-4 py-2.5 focus:outline-none"
              style={inputStyle}
              placeholder="João Silva"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </div>

          <div>
            <label className="block mb-2" style={labelStyle}>
              Senha <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              type="password"
              required
              className="w-full px-4 py-2.5 focus:outline-none"
              style={inputStyle}
              placeholder="Mínimo 8 caracteres"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>

          <div>
            <label className="block mb-2" style={labelStyle}>
              Confirmar senha <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              type="password"
              required
              className="w-full px-4 py-2.5 focus:outline-none"
              style={inputStyle}
              placeholder="Digite a senha novamente"
              value={form.confirmPassword}
              onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
            />
          </div>

          <div>
            <label className="block mb-2" style={labelStyle}>
              Endereço
            </label>
            <input
              type="text"
              className="w-full px-4 py-2.5 focus:outline-none"
              style={inputStyle}
              placeholder="Rua, número, cidade, estado"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>

          <div>
            <label className="block mb-2" style={labelStyle}>
              Perfil <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <select
              required
              className="w-full px-4 py-2.5 focus:outline-none"
              style={inputStyle}
              value={form.professionalProfile}
              onChange={(e) => setForm({ ...form, professionalProfile: e.target.value as ProfessionalProfile })}
            >
              <option value="">Selecione um perfil</option>
              {PROFESSIONAL_PROFILES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {requiresOAB && (
            <>
              <div>
                <label className="block mb-2" style={labelStyle}>
                  Número da OAB <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  required={requiresOAB}
                  className="w-full px-4 py-2.5 focus:outline-none"
                  style={inputStyle}
                  placeholder="123456"
                  value={form.oabNumber}
                  onChange={(e) => setForm({ ...form, oabNumber: e.target.value })}
                />
              </div>
              <div>
                <label className="block mb-2" style={labelStyle}>
                  UF da OAB <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  required={requiresOAB}
                  className="w-full px-4 py-2.5 focus:outline-none"
                  style={inputStyle}
                  value={form.oabState}
                  onChange={(e) => setForm({ ...form, oabState: e.target.value })}
                >
                  <option value="">Selecione a UF</option>
                  {BRAZILIAN_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="flex items-start flex-shrink-0">
            <input
              type="checkbox"
              id="terms-invite"
              checked={form.termsAccepted}
              onChange={(e) => setForm({ ...form, termsAccepted: e.target.checked })}
              className="mt-1 mr-2"
              style={{ accentColor: '#233444' }}
            />
            <label htmlFor="terms-invite" style={{ fontSize: '12px', color: '#2D3748' }}>
              Li e aceito os{' '}
              <button type="button" onClick={() => openTermsModal('terms')} className="text-[#233444] hover:underline">
                Termos de Uso
              </button>
              {' '}e{' '}
              <button type="button" onClick={() => openTermsModal('privacy')} className="text-[#233444] hover:underline">
                Política de Privacidade
              </button>
              <span style={{ color: '#EF4444' }}> *</span>
            </label>
          </div>

          {error && (
            <div ref={errorRef} className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex-shrink-0" style={{ fontSize: '12px' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={registerMutation.isPending}
            className="w-full py-2.5 px-4 text-white font-medium rounded-[20px] transition-colors disabled:opacity-50 flex-shrink-0 mt-auto"
            style={{
              backgroundColor: '#233444',
              fontFamily: "'Poppins', sans-serif",
              fontSize: '12px',
              boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
            }}
            onMouseEnter={(e) => {
              if (!registerMutation.isPending) e.currentTarget.style.backgroundColor = '#1a2833';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#233444';
            }}
          >
            {registerMutation.isPending ? 'Criando conta...' : 'Criar conta'}
          </button>
        </form>
          </div>
        </div>
      </div>

      <TermsModal isOpen={termsModalOpen} onClose={() => setTermsModalOpen(false)} type="terms" />
      <TermsModal isOpen={privacyModalOpen} onClose={() => setPrivacyModalOpen(false)} type="privacy" />

      {/* Logo no rodapé */}
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      >
        <img src="/logo-preta.svg" alt="Prometeus" style={{ width: '24px', height: '24px' }} />
      </div>
    </>
  );
}
